import React, { useEffect, useRef, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { Loader2, CheckCircle2, AlertOctagon, ArrowRight, Sparkles } from "lucide-react";
import Header from "../components/Header";
import Footer from "../components/Footer";
import { useUserAuth } from "../lib/userAuth";

import useDocumentTitle from "../hooks/useDocumentTitle";

// Lookup table mirrors backend PLANS so we can display a friendly name
// before the /me refresh round-trips. Source of truth is still server-side.
const SKU_LABEL = {
  audit_oneshot:        { label: "One-Shot",          credits: 1,   cycle: "one-time" },
  audit_builder_monthly:{ label: "Builder · Monthly", credits: 3,   cycle: "monthly"  },
  audit_builder_annual: { label: "Builder · Annual",  credits: 36,  cycle: "annual"   },
  audit_team_monthly:   { label: "Team · Monthly",    credits: 15,  cycle: "monthly"  },
  audit_team_annual:    { label: "Team · Annual",     credits: 180, cycle: "annual"   },
};

export default function AuditCheckoutSuccess() {
  useDocumentTitle('Audit Order Confirmed — QorTrace');
  const [params] = useSearchParams();
  const sessionId = params.get("session_id") || "";
  const { client, refresh: refreshAccount } = useUserAuth();
  const [state, setState] = useState({ status: "polling", payment_status: "", plan_id: "" });
  const [error, setError] = useState("");
  const attemptsRef = useRef(0);

  useEffect(() => {
    if (!sessionId) { setError("Missing session id."); return; }
    let cancelled = false;
    const MAX_ATTEMPTS = 10;
    const INTERVAL_MS = 2000;

    const poll = async () => {
      if (cancelled) return;
      attemptsRef.current += 1;
      try {
        const { data } = await client.get(`/billing/status/${sessionId}`);
        setState(data);
        if (data.status === "paid") {
          refreshAccount();
          return;
        }
        if (data.status === "expired" || data.status === "failed") return;
        if (attemptsRef.current >= MAX_ATTEMPTS) {
          setError("Still processing — refresh this page in a minute, or check your email for a receipt.");
          return;
        }
        setTimeout(poll, INTERVAL_MS);
      } catch (e) {
        setError(e?.response?.data?.detail || "Could not verify payment.");
      }
    };
    poll();
    return () => { cancelled = true; };
    // eslint-disable-next-line
  }, [sessionId]);

  const meta = SKU_LABEL[state.plan_id] || null;

  return (
    <div className="min-h-screen qt-radial-bg" data-testid="audit-checkout-success-page">
      <Header />
      <section className="max-w-[800px] mx-auto px-6 py-20">
        <div className="qt-card" style={{ border: "1px solid var(--qt-cyan)", padding: "2rem" }}>
          {error ? (
            <div data-testid="audit-checkout-error">
              <div className="flex items-center gap-2">
                <AlertOctagon size={16} style={{ color: "#FF6E6E" }} />
                <span className="qt-label" style={{ color: "#FF6E6E" }}>SOMETHING WENT WRONG</span>
              </div>
              <p className="text-sm mt-3" style={{ color: "var(--qt-text-2)" }}>{error}</p>
              <Link to="/pricing/audits" className="qt-btn qt-btn-secondary mt-4 inline-flex">
                Back to audit pricing
              </Link>
            </div>
          ) : state.status === "paid" ? (
            <div data-testid="audit-checkout-paid">
              <div className="flex items-center gap-2">
                <CheckCircle2 size={16} style={{ color: "#1FDD83" }} />
                <span className="qt-label" style={{ color: "#1FDD83" }}>
                  PAYMENT SUCCESSFUL · {meta ? meta.label.toUpperCase() : (state.plan_id || "").toUpperCase()}
                </span>
              </div>
              <h1 className="qt-display text-3xl mt-2" style={{ color: "#fff" }}>
                You're cleared for audit.
              </h1>
              <p className="mt-3" style={{ color: "var(--qt-text-2)", lineHeight: 1.6 }}>
                {meta?.cycle === "one-time"
                  ? `${meta.credits} audit credit has been added to your account. Submit your contract whenever you're ready — Standard tier delivers in <90s.`
                  : meta
                    ? `${meta.credits} audit credits added for the ${meta.cycle} cycle. Use them whenever — fresh credits arrive at the next renewal.`
                    : "Your audit plan is active. Submit a contract whenever you're ready."
                }
              </p>
              <div className="mt-6 flex gap-3 flex-wrap">
                <Link to="/account/audits/new"
                      data-testid="audit-checkout-submit-cta"
                      className="qt-btn qt-btn-primary">
                  <Sparkles size={14} /> Submit my first audit <ArrowRight size={14} />
                </Link>
                <Link to="/account/audits"
                      data-testid="audit-checkout-list-cta"
                      className="qt-btn qt-btn-secondary">
                  My audits
                </Link>
                <Link to="/account/billing"
                      data-testid="audit-checkout-billing-cta"
                      className="qt-btn qt-btn-secondary">
                  View billing
                </Link>
              </div>
            </div>
          ) : state.status === "expired" || state.status === "failed" ? (
            <div data-testid="audit-checkout-failed">
              <div className="flex items-center gap-2">
                <AlertOctagon size={16} style={{ color: "#FFB000" }} />
                <span className="qt-label" style={{ color: "#FFB000" }}>SESSION {state.status?.toUpperCase()}</span>
              </div>
              <p className="mt-3" style={{ color: "var(--qt-text-2)" }}>
                The checkout session expired or the payment didn't go through. No charge was made.
              </p>
              <Link to="/pricing/audits" className="qt-btn qt-btn-primary mt-4 inline-flex">
                Try again
              </Link>
            </div>
          ) : (
            <div data-testid="audit-checkout-polling">
              <div className="flex items-center gap-2">
                <Loader2 size={16} className="animate-spin" style={{ color: "var(--qt-cyan)" }} />
                <span className="qt-label" style={{ color: "var(--qt-cyan)" }}>VERIFYING PAYMENT…</span>
              </div>
              <p className="mt-3" style={{ color: "var(--qt-text-2)" }}>
                We're confirming with Stripe. This usually takes a few seconds — don't close this page.
              </p>
            </div>
          )}
        </div>
      </section>
      <Footer />
    </div>
  );
}
